import type { HomePageCarouselItem } from '@bulknutrients/bulk-shared-lib/interfaces/ICarousel';
import type {
  CarouselItemReferencedProduct,
  CarouselItemReferencedProductVariant,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import { removeUndefined } from '@bulknutrients/bulk-shared-lib/utils';
import { AddToCart, AddToFavourites, Button } from '@components/Button';
import { CustomImage } from '@components/Media/Image';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useStockLevel } from '@hooks/useStockLevel';
import { useWindowSize } from '@hooks/useWindowSize';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

const ActionButtonStyles =
  'home-cta-actions mb-5 flex items-stretch gap-4 flex-col sm:flex-row sm:items-end sm:mb-0';

function ProductActions({
  product,
  productVariant,
  ctaPrimaryLink,
  ctaPrimaryLinkText,
  ctaSecondaryLink,
  ctaSecondaryLinkText,
}: {
  product: CarouselItemReferencedProduct;
  productVariant?: CarouselItemReferencedProductVariant;
  ctaPrimaryLinkText?: string;
  ctaPrimaryLink?: string;
  ctaSecondaryLinkText?: string;
  ctaSecondaryLink?: string;
}) {
  const [shouldCheckStock, setShouldCheckStock] = useState(false);
  const { inStock } = useStockLevel(product.productId.toString(), {
    enabled: shouldCheckStock,
  });
  // default to show product in stock until fetched to reduce CLS. If vice versa and product is OOS, unlikely a user will be able select before showing.
  // if query fails and is OOS, but shows in stock here, still won't be able to checkout so taking the risk
  const showInStock = inStock !== undefined ? inStock : true;

  const isDigital = useRef(product.sku.includes('BN-EBOOK'));
  const variantId = productVariant
    ? productVariant.variantId
    : product.defaultVariant.variantId;

  return (
    <div className={ActionButtonStyles}>
      {ctaPrimaryLink && (
        <Button
          className="home-cta-primary"
          href={ctaPrimaryLink}
          data-umami-event-location="CarouselItem ctaPrimaryLink"
          data-umami-event-product-id={product.productId}
          data-umami-event-variant-id={variantId}
        >
          {ctaPrimaryLinkText}
        </Button>
      )}
      {showInStock && !isDigital.current && !ctaPrimaryLink && (
        <AddToCart
          productId={product.productId}
          name={product.name}
          variantId={variantId || product.defaultVariant.variantId}
          sku={productVariant?.sku || product.sku}
          quantity={1}
          className="w-full"
          data-umami-event-location="CarouselItem"
          onMouseEnter={() => setShouldCheckStock(true)}
          onFocus={() => setShouldCheckStock(true)}
          onTouchStart={() => setShouldCheckStock(true)}
        />
      )}
      {ctaSecondaryLink && (
        <Button
          className="home-cta-secondary"
          href={ctaSecondaryLink}
          buttonStyle="secondary"
          data-umami-event-location="CarouselItem ctaSecondaryLink"
          data-umami-event-product-id={product.productId}
          data-umami-event-variant-id={variantId}
        >
          {ctaSecondaryLinkText}
        </Button>
      )}
      {!ctaSecondaryLink && (
        <AddToFavourites
          sku={product.sku}
          fullWidth
          productId={product.productId}
          data-umami-event-location="carouselItem"
        />
      )}
    </div>
  );
}

function ActionButtons({
  carouselItem,
}: {
  carouselItem: HomePageCarouselItem;
}) {
  const {
    referencedDocument,
    referencedProductVariant,
    ctaPrimaryLink,
    ctaPrimaryLinkText,
    ctaSecondaryLink,
    ctaSecondaryLinkText,
  } = carouselItem;

  // Document is references
  if (referencedDocument?.type === 'product') {
    return (
      <ProductActions
        product={referencedDocument as CarouselItemReferencedProduct}
        productVariant={referencedProductVariant}
        ctaPrimaryLink={ctaPrimaryLink}
        ctaPrimaryLinkText={ctaPrimaryLinkText}
        ctaSecondaryLink={ctaSecondaryLink}
        ctaSecondaryLinkText={ctaSecondaryLinkText}
      />
    );
  }

  return (
    <div className={ActionButtonStyles}>
      {/*
      use the slug from the referenced document for the primary link (with whatever text is specified for it),
      unless there is a URL specified in the Primary link URL field */}
      {ctaPrimaryLinkText && (
        <Button
          className="home-cta-primary"
          href={ctaPrimaryLink || referencedDocument?.slug}
          data-umami-event-location="homePageCarousel ctaPrimaryLink"
        >
          {ctaPrimaryLinkText}
        </Button>
      )}
      {ctaSecondaryLink && (
        <Button
          className="home-cta-secondary"
          href={ctaSecondaryLink}
          buttonStyle="secondary"
          data-umami-event-location="homePageCarousel ctaSecondaryLink"
        >
          {ctaSecondaryLinkText}
        </Button>
      )}
    </div>
  );
}

// get the appropriate link for headline
const getHeadlineLink = (ci: HomePageCarouselItem) => {
  if (ci.ctaPrimaryLink) return ci.ctaPrimaryLink;
  if (ci.referencedDocument) return ci.referencedDocument.slug;
  return '';
};

function getHeightForWidthAndAspectRatio(
  imageWidth: number,
  aspectRatio: string | undefined
) {
  if (!aspectRatio) return undefined;
  const [widthRatio, heightRatio] = aspectRatio.split(':').map(Number);
  if (Number.isNaN(widthRatio) || Number.isNaN(heightRatio)) return undefined;
  return Math.round((imageWidth * heightRatio) / widthRatio);
}

export function CarouselItem({
  carouselItem,
  priority,
}: {
  carouselItem: HomePageCarouselItem;
  priority: boolean;
}) {
  const { width } = useWindowSize();
  const [aspectRatio, setAspectRatio] = useState<string>('1:1');
  const [imageWidthToUse, setImageWidthToUse] = useState<number>(
    Math.min(carouselItem.image.width, 2500)
  );
  const [imageHeightToUse, setImageHeightToUse] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (width) {
      setImageWidthToUse(Math.min(carouselItem.image.width, width));

      if (width < 640) {
        setAspectRatio('1:1');
      } else if (width < 1024) {
        setAspectRatio('4:3');
      } else {
        setAspectRatio('2:1');
      }
    } else {
      setAspectRatio('1:1');
    }
  }, [width, carouselItem.image.width]);

  useEffect(() => {
    setImageHeightToUse(
      getHeightForWidthAndAspectRatio(imageWidthToUse, aspectRatio)
    );
  }, [imageWidthToUse, aspectRatio]);

  return (
    <div className="relative flex h-full flex-col lg:block">
      {carouselItem.image && (
        <div className="relative mx-auto my-0 overflow-hidden w-screen">
          {carouselItem.image && (
            <CustomImage
              image={carouselItem.image}
              width={imageWidthToUse}
              className="rounded-none"
              height={imageHeightToUse}
              priority={priority}
              templateOverride={removeUndefined({
                crop: 'auto',
                gravity: 'auto',
                aspectRatio: aspectRatio,
              })}
            />
          )}
        </div>
      )}
      <div
        data-tracking-info={carouselItem.ctaName}
        className={twJoin([
          'home-carousel-item',
          'relative lg:absolute lg:top-[65px] lg:right-[6vw] xl:top-[120px]',
          'overflow-visible lg:overflow-hidden',
          'lg:max-h-[calc(100%-130px)] lg:w-fit xl:max-h-[calc(100%-170px)]',
          'rounded-3xl',
        ])}
      >
        <div className="relative mx-auto lg:bg-white lg:dark:bg-black my-0 flex h-full w-full lg:w-[50vw] gap-2.5 ">
          <div className="z-2 h-full grow lg:p-12.5 p-6">
            <div
              className="absolute mb-3 inline-block 
            -translate-y-2/4 translate-x-0 bg-black dark:bg-grey-dark pb-0.5 pl-2.5
            pr-5 pt-0.5 font-secondary uppercase tracking-wide text-white sm:text-xl xl:text-3xl rounded-r-2xl"
            >
              {carouselItem.postType}
            </div>
            <TextLink
              href={getHeadlineLink(carouselItem)}
              className="home-cta-title block no-underline hover:no-underline"
              data-umami-event-location="carouselItem headline"
            >
              <h2
                className="mx-0 my-5 pt-5 font-primary text-4xl italic 
              tracking-tighter text-black dark:text-white sm:text-5xl xl:text-6xl"
              >
                {carouselItem.ctaHeadline}
              </h2>
            </TextLink>
            {carouselItem.ctaDescription && (
              <PortableTextBlockRenderer
                content={carouselItem.ctaDescription}
                componentsOverride={{
                  block: {
                    normal: ({ children }: { children?: ReactNode }) => (
                      <p
                        className="mx-0 mb-5 mt-2.5 text-lg text-grey-dark
                      dark:text-white xl:text-xl"
                      >
                        {children}
                      </p>
                    ),
                  },
                }}
              />
            )}
            <ActionButtons carouselItem={carouselItem} />
          </div>
        </div>
      </div>
    </div>
  );
}
